import {
  ErrorMessage,
  FormGroup,
  FormNote,
  getGlobalFormConfig,
  Hint,
  Icon,
  Label,
  Select,
  useFormConfig,
} from '@ver-uds/uswds-react';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, {
  ChangeEvent,
  forwardRef,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PageLoader from '../../PageLoader';
import {
  CLEAR_INPUT_BUTTON_CLASS,
  CLEAR_INPUT_BUTTON_WRAPPER_CLASS,
  COMBO_BOX_CLASS,
  COMBO_BOX_PRISTINE_CLASS,
  INPUT_BUTTON_SEPARATOR_CLASS,
  INPUT_CLASS,
  LIST_CLASS,
  LIST_OPTION_CLASS,
  LIST_OPTION_NO_RESULTS,
  LIST_OPTION_SELECTED_CLASS,
  SELECT_CLASS,
  TOGGLE_LIST_BUTTON_CLASS,
  TOGGLE_LIST_BUTTON_WRAPPER_CLASS,
} from './ComboBoxClasses';
import useComboBoxApi, { SearchOption } from './useComboBoxApi';

export interface ComboBoxApiProps {
  options: Array<SearchOption> | undefined;
  /** Interval (in ms) determining how long the debounce time is
   * @default 250 ms
   */
  debounceInterval?: number;
  /** The number of options to be shown after a successful search query
   * @default 9 options
   */
  numOptions?: number;
  /** Sets loading state for list of options */
  isLoading?: boolean;
  /** Sets the minimum length required for the search function to be invoked
   * @default 3 characters
   */
  minLength?: number;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  errorId?: string;
  errorMessage?: string;
  hint?: string;
  id: string;
  label: string;
  labelHint?: ReactNode;
  name?: string;
  note?: ReactNode;
  /**
   * Function that is invoked when the value of the hidden Select input changes.
   */
  onChange?: (value: string) => void;
  /**
   * Function that is invoked when the value of the text input changes.
   */
  onSearchChange?: (value: string | undefined) => void;
  required?: boolean;
  requiredLabel?: ReactNode;
  value?: string;
  placeholder?: string;
}

/**
 * WARNING: This component is still in Beta and may experience breaking changes. Use at your own risk.
 */
const ComboBoxApi = forwardRef<HTMLSelectElement, ComboBoxApiProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedBy,
      className,
      debounceInterval = 250,
      defaultValue,
      disabled,
      errorId,
      errorMessage,
      hint,
      id,
      isLoading,
      label,
      labelHint,
      minLength = 3,
      name,
      note,
      numOptions = 9,
      onChange: selectedValueChange = () => {},
      placeholder,
      required,
      requiredLabel,
      options,
      value: controlledValue,
      onSearchChange = () => {},
    }: ComboBoxApiProps,
    ref,
  ): JSX.Element => {
    const [showClearButton, setShowClearButton] = useState<boolean>(() => Boolean(controlledValue));
    const [isClean, setIsClean] = useState<boolean>(true);
    const focusRef = useRef<HTMLLIElement | null>(null);
    const baseInputRef = useRef<HTMLInputElement | null>(null);
    const { value, setValue, debounce, searchOptions } = useComboBoxApi(
      options,
      numOptions,
      debounceInterval,
      minLength,
      onSearchChange,
      controlledValue,
    );

    const onInputChange = (input: string) => {
      setValue(input);
    };

    const {
      highlightedIndex,
      isOpen,
      inputValue: comboBoxValue,
      selectedItem,
      closeMenu,
      getComboboxProps,
      getInputProps,
      getItemProps,
      getLabelProps,
      getMenuProps,
      getToggleButtonProps,
      openMenu,
      reset,
      selectItem,
      setInputValue,
      setHighlightedIndex,
    } = useCombobox({
      defaultIsOpen: false,
      id,
      inputValue: value,
      items: searchOptions,
      onInputValueChange: ({ inputValue }) => {
        debounce(inputValue);
      },
      onSelectedItemChange: ({ selectedItem: item }) => {
        onInputChange(item?.displayName ? item.displayName : item?.value ?? '');
        selectedValueChange(item?.value ?? '');
      },
      onStateChange: ({ inputValue, type }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.FunctionSelectItem:
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
            setShowClearButton(true);
            closeMenu();
            break;
          case useCombobox.stateChangeTypes.InputChange:
            if (inputValue !== value) {
              debounce(inputValue);
            }
            if (inputValue || inputValue === '') {
              onInputChange(inputValue);
            }
            setShowClearButton(false);
            break;
          case useCombobox.stateChangeTypes.FunctionReset:
            debounce(undefined);
            onInputChange('');
            setInputValue('');
            setShowClearButton(false);
            if (ref) (ref as MutableRefObject<HTMLSelectElement>)?.current?.focus();
            break;
          case useCombobox.stateChangeTypes.InputBlur:
            closeMenu();
            break;
          default:
            break;
        }
      },
      stateReducer: (state, { type, changes }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputBlur:
            if (changes.selectedItem !== undefined) {
              const { selectedItem: _item, ...rest } = changes;
              return {
                ...rest,
                inputValue: state.inputValue,
                selectItem: false,
                isOpen: true,
                highlightedIndex: state?.highlightedIndex ?? -1,
              };
            }
            return { ...changes, selectItem: false, highlightedIndex: state?.highlightedIndex };
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.FunctionSelectItem:
            return {
              ...changes,
              inputValue: changes.selectedItem?.displayName
                ? changes.selectedItem.displayName
                : changes?.selectedItem?.value,
              selectedItem: changes.selectedItem,
              selectItem: true,
              isOpen: false,
            };
          case useCombobox.stateChangeTypes.ItemClick:
            setShowClearButton(true);
            return {
              ...changes,
              inputValue: changes?.selectedItem?.displayName
                ? changes?.selectedItem.displayName
                : changes?.selectedItem?.value ?? '',
              isOpen: false,
              selectItem: true,
            };
          default:
            return changes;
        }
      },
    });

    const shouldFocusOption = highlightedIndex !== null && highlightedIndex > -1;

    // Show clear button on initial render when defaultValue is set
    useEffect(() => {
      if (isClean && defaultValue) {
        selectItem({ value: defaultValue });
        setHighlightedIndex(0);
        setInputValue(defaultValue);
        setShowClearButton(true);
        setIsClean(false);
      }
    }, [options, defaultValue, selectItem, setHighlightedIndex, setInputValue, isClean]);

    useEffect(() => {
      if (focusRef?.current && shouldFocusOption) {
        focusRef?.current?.focus({ preventScroll: true });
      }
    }, [focusRef, shouldFocusOption, highlightedIndex]);

    const listOptions = useMemo(() => {
      if (isLoading) {
        return <PageLoader label="Loading options" noOverlay />;
      }

      return (
        <>
          {searchOptions?.length > 0 ? (
            <>
              {searchOptions?.length >= numOptions && (
                <li
                  className={classNames(
                    LIST_OPTION_CLASS,
                    LIST_OPTION_NO_RESULTS,
                    'ver-combo-box-api__option-results-overflow',
                  )}
                >
                  <Icon name="filter_list" /> <div className="margin-left-2">Type to narrow your search</div>
                </li>
              )}
              {searchOptions?.map((option, index) => {
                const isFocused = highlightedIndex === index;
                return (
                  <li
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getItemProps({
                      item: option,
                      index,
                      onMouseEnter: (event: React.MouseEvent<HTMLLIElement>) => {
                        if (focusRef?.current) {
                          focusRef.current = event.currentTarget;
                        }
                      },
                      onKeyPress: (event) => {
                        if (event.key === 'Enter') {
                          setInputValue(option?.displayName ? option.displayName : option.value);
                          setHighlightedIndex(index);
                          selectItem(option);
                        }
                      },
                      onClick: (event) => {
                        event.preventDefault();
                        const element = option?.displayName ? option.displayName : option.value;
                        setInputValue(element);
                        setHighlightedIndex(index);
                        selectItem(option);
                      },
                    })}
                    role="option"
                    aria-selected={
                      selectedItem?.value === option.value || option.displayName === selectedItem?.displayName
                    }
                    className={classNames({
                      [LIST_OPTION_CLASS]: true,
                      'ver-combo-box-api__option-focused': isFocused,
                      [LIST_OPTION_SELECTED_CLASS]:
                        selectedItem?.value === option.value || option.displayName === selectedItem?.displayName,
                    })}
                    // disable eslint rule because downshift uses the index in the key for internal things
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${option.value}-${index}`}
                    value={option.value}
                    tabIndex={
                      selectedItem?.value === option.value || option.displayName === selectedItem?.displayName ? 0 : -1
                    }
                  >
                    {option?.displayName ?? option.value}
                  </li>
                );
              })}
            </>
          ) : (
            <li role="option" aria-selected="false" className={LIST_OPTION_NO_RESULTS}>
              No results found
            </li>
          )}
        </>
      );
    }, [
      getItemProps,
      highlightedIndex,
      isLoading,
      numOptions,
      searchOptions,
      selectItem,
      selectedItem?.displayName,
      selectedItem?.value,
      setHighlightedIndex,
      setInputValue,
    ]);

    const { requiredLabel: contextRequiredLabel } = useFormConfig();
    const { requiredLabel: globalRequiredLabel } = getGlobalFormConfig();
    const finalRequiredLabel = requiredLabel || contextRequiredLabel || globalRequiredLabel;

    return (
      <FormGroup error={errorId && errorMessage} className={className}>
        <div
          className={classNames('ver-combo-box-api', className, {
            [COMBO_BOX_CLASS]: true,
            [COMBO_BOX_PRISTINE_CLASS]: showClearButton,
          })}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Label {...getLabelProps()}>
            {label}
            {required ? <> {finalRequiredLabel}</> : null}
            {labelHint ? (
              <>
                {' '}
                <Hint id={`${id}-label-hint`}>{labelHint}</Hint>
              </>
            ) : null}
          </Label>
          {hint ? <Hint id={`${id}-hint`}>{hint}</Hint> : null}
          {errorId && errorMessage ? <ErrorMessage id={errorId}>{errorMessage}</ErrorMessage> : null}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <div {...getComboboxProps()} name={name} className={classNames(className, COMBO_BOX_CLASS)}>
            <Select
              ref={ref}
              id=""
              name={name}
              value={selectedItem?.value}
              className={classNames(SELECT_CLASS, 'usa-sr-only')}
              aria-hidden
              tabIndex={-1}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => onInputChange(event.target.value)}
            >
              {searchOptions?.map(({ value: optionValue }) => (
                <option key={optionValue} value={optionValue}>
                  {optionValue}
                </option>
              ))}
            </Select>
            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getInputProps({
                ref: baseInputRef,
              })}
              aria-controls={`${id}-menu`}
              aria-describedby={ariaDescribedBy}
              aria-expanded={isOpen}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledby}
              aria-required={required}
              autoCapitalize="off"
              autoComplete="off"
              role="combobox"
              className={INPUT_CLASS}
              disabled={disabled}
              placeholder={placeholder}
              required={required}
              type="text"
              value={comboBoxValue}
              onClick={() => {
                openMenu();
              }}
            />
            <span className={CLEAR_INPUT_BUTTON_WRAPPER_CLASS}>
              <button
                type="button"
                className={CLEAR_INPUT_BUTTON_CLASS}
                onClick={() => {
                  reset();
                  baseInputRef?.current?.focus();
                  debounce('');
                }}
                aria-label="Clear the selected item"
                disabled={disabled}
                hidden={!selectedItem}
              />
            </span>
            <span className={INPUT_BUTTON_SEPARATOR_CLASS}>&nbsp;</span>
            <span className={TOGGLE_LIST_BUTTON_WRAPPER_CLASS}>
              <button
                type="button"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...getToggleButtonProps()}
                aria-label="Toggle the dropdown list"
                className={TOGGLE_LIST_BUTTON_CLASS}
                disabled={disabled}
              />
            </span>
          </div>
          <ul
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getMenuProps({
              onKeyPress: (event) => {
                if ((isOpen && event.key === 'ArrowDown') || event.key === 'ArrowUp') {
                  event.preventDefault();
                }
              },
            })}
            className={LIST_CLASS}
            hidden={!isOpen}
            tabIndex={-1}
            aria-labelledby={`${id}-label`}
          >
            {isOpen ? listOptions : null}
          </ul>
        </div>
        {note ? <FormNote>{note}</FormNote> : null}
      </FormGroup>
    );
  },
);

export default ComboBoxApi;
